import { formatDateFromISO } from '@practium/shared';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Spa from '@mui/icons-material/Spa';
import { BrainIcon, DetailModal, FormField, FormInput, FormSelect } from '@practium/ui';
import type { DetailSection } from '@practium/ui';

import styles from './ProductRequestModals.module.css';
import { CATEGORY_OPTIONS } from '../../hooks/useProductRequest';
import {
	ProductRequestFormData,
	CATEGORY_LABELS,
	STATUS_LABELS,
	RequestProductCategory,
} from '../../types/productRequest.types';
import { RequestProduct } from '@practium/features/products/types';

interface ProductRequestModalsProps {
	selectedRequest: RequestProduct | null;
	onCloseDetail: () => void;
	isCreateOpen: boolean;
	onCloseCreate: () => void;
	onSubmit: () => void;
	formData: ProductRequestFormData;
	onFieldChange: <K extends keyof ProductRequestFormData>(
		field: K,
		value: ProductRequestFormData[K]
	) => void;
	formErrors: Record<string, string>;
	onClearError: (field: string) => void;
	currentDate: string;
	userName: string;
}

export const ProductRequestModals = ({
	selectedRequest,
	onCloseDetail,
	isCreateOpen,
	onCloseCreate,
	onSubmit,
	formData,
	onFieldChange,
	formErrors,
	onClearError,
	currentDate,
	userName,
}: ProductRequestModalsProps) => {
	const buildViewSections = (req: RequestProduct): DetailSection[] => [
		{
			title: 'Informações Gerais',
			fields: [
				{ label: 'Nome', value: req.name },
				{ label: 'Fabricante', value: req.manufacturer },
				{ label: 'Categoria', value: CATEGORY_LABELS[req.category] },
				{ label: 'Data', value: formatDateFromISO(req.createdAt) },
				{ label: 'Status', value: STATUS_LABELS[req.status] },
			],
		},
	];

	const editContent = (
		<div className={styles.editGrid}>
			<FormField label="Nome" required error={formErrors.name}>
				<FormInput
					value={formData.name}
					onChange={(e) => {
						onFieldChange('name', e.target.value);
						onClearError('name');
					}}
					placeholder="Ex.: Calcário Dolomítico"
					hasError={!!formErrors.name}
				/>
			</FormField>
			<FormField label="Fabricante" required error={formErrors.manufacturer}>
				<FormInput
					value={formData.manufacturer}
					onChange={(e) => {
						onFieldChange('manufacturer', e.target.value);
						onClearError('manufacturer');
					}}
					placeholder="Ex.: Votorantim"
					hasError={!!formErrors.manufacturer}
				/>
			</FormField>
			<div className={styles.editGridFull}>
				<FormField label="Categoria" required error={formErrors.category}>
					<FormSelect
						value={formData.category}
						onChange={(v) => {
							onFieldChange('category', v as RequestProductCategory);
							onClearError('category');
						}}
						options={CATEGORY_OPTIONS}
						placeholder="Selecione a categoria"
						hasError={!!formErrors.category}
					/>
				</FormField>
			</div>
			<div className={styles.editGridFull}>
				<FormField label="Observações">
					<textarea
						className={styles.textarea}
						value={formData.note}
						onChange={(e) => onFieldChange('note', e.target.value)}
						placeholder="Ex.: Esse produto é importante para..."
						rows={6}
					/>
				</FormField>
			</div>
		</div>
	);

	const createSections: DetailSection[] = [
		{ title: 'Informações Gerais', fields: [], editContent },
	];

	return (
		<>
			{selectedRequest && (
				<DetailModal
					title={selectedRequest.name}
					metaItems={[
						{
							icon: (
								<CalendarToday className={styles.metaIcon} style={{ color: 'var(--primary)' }} />
							),
							text: formatDateFromISO(selectedRequest.createdAt),
						},
						{
							icon: <Spa className={styles.metaIcon} style={{ color: 'var(--primary)' }} />,
							text: 'Solicitação de Produto',
						},
						{
							icon: <BrainIcon color="var(--primary)" />,
							text: `Por ${selectedRequest.userName}`,
						},
					]}
					sections={buildViewSections(selectedRequest)}
					mode="view"
					onClose={onCloseDetail}
				/>
			)}

			{isCreateOpen && (
				<DetailModal
					title="Nova Solicitação de Produto"
					metaItems={[
						{
							icon: (
								<CalendarToday className={styles.metaIcon} style={{ color: 'var(--primary)' }} />
							),
							text: currentDate,
						},
						{
							icon: <Spa className={styles.metaIcon} style={{ color: 'var(--primary)' }} />,
							text: 'Solicitação de Produto',
						},
						{
							icon: <BrainIcon color="var(--primary)" />,
							text: `Por ${userName}`,
						},
					]}
					sections={createSections}
					mode="create"
					onClose={onCloseCreate}
					onSubmit={onSubmit}
				/>
			)}
		</>
	);
};
